export const FakeData = [
  {
    id: 1,
    name: "New Conversation 1",
    chatData: [
      {
        sender: "me",
        message: "Explain the Data Access layer used in the project?",
      },
      {
        sender: "ai",
        message: `
# Data Access Layer Overview

The Data Access layer in the project is implemented using the Data Access Object (DAO) pattern, which provides an abstract interface to the underlying database. This pattern allows for separating the persistence logic from the business logic, making the code more maintainable and testable.

The DAOs in this project are responsible for performing CRUD (Create, Read, Update, Delete) operations on entities such as Film, Actor, Language, etc. The project uses Hibernate as the ORM (Object-Relational Mapping) tool to interact with the database. Hibernate maps Java objects to database tables and vice versa, allowing developers to work with objects rather than SQL queries directly.

## Key Components in the Data Access Layer

### 1. HibernateUtil (io.peerislands.util.HibernateUtil)
- This utility class is responsible for creating and managing the SessionFactory object, which is a heavyweight object that creates Session instances for interacting with the database.
- It sets up the configuration properties for Hibernate, such as the database connection URL, user credentials, dialect, and more.
- It also adds annotated classes to the configuration, which Hibernate uses to map entities to database tables.

### 2. DAO Classes
- Each DAO class corresponds to an entity and provides methods to perform database operations related to that entity.
- For example, FilmDao (io.peerislands.dao.FilmDao) provides methods like saveFilm, updateFilm, deleteFilm, getFilmById, and getFilms to manage Film entities.
- DAO classes use the Session object provided by HibernateUtil to begin transactions, execute queries, and commit or rollback transactions as needed.

### 3. Entity Classes
- Entity classes such as Film, Actor, Language, etc., are annotated with JPA (Java Persistence API) annotations that define the mapping between the class and the database table.
- Annotations like @Entity, @Table, @Id, @Column, @ManyToOne, @OneToMany, etc., are used to specify the table name, primary keys, columns, and relationships between entities.

### 4. Service Layer (io.peerislands.service.FilmService)
- Although not part of the DAO layer, the service layer interacts with it. The service layer provides higher-level business logic and uses DAOs to access and manipulate data.
- For example, FilmService provides methods to get all films, get a film by ID, insert a new film, update a film, delete a film, and get actors by film name.

## Hotspots

### HibernateUtil
- Configuration and SessionFactory setup: [HibernateUtil: io.peerislands.util.HibernateUtil.java:31-70](31:HibernateUtil.java)

### FilmDao
- CRUD operations for [FilmDao: io.peerislands.dao.FilmDao.java:19-210](19:FilmDao.java)

### LanguageDao
- Fetching Language by ID: [Film: io.peerislands.dao.LanguageDao.java:11-80](11:LanguageDao.java)

### ActorDao
- CRUD operations for Actor: [Actor: io.peerislands.dao.ActorDao.java:18-150](18:ActorDao.java)

### Entity Classes
- [Film entity mapping: io.peerislands.entity.Film.java:28-240](17:Film.java)
- [Actor entity mapping: io.peerislands.entity.Actor.java:18-140](18:Actor.java)
- [Language entity mapping: io.peerislands.entity.Language.java:16-60](16:Language.java)

### Service Layer
[FilmService methods interacting with FilmDao: io.peerislands.service.FilmService.java:14-110](14:FilmService.java)

The DAO classes are designed to be used with a try-with-resources statement, as they implement the AutoCloseable interface, ensuring that resources like Session are properly closed after use.
`,
      },
    ],
  },
];
// FakeJava.js
export const FakeJava = `
package io.peerai.demo.dao;
import io.peerai.demo.dto.ActorDTO;
import io.peerai.demo.dto.MovieActorDTO;
import org.springframework.jdbc.core.JdbcTemplate;
import org.springframework.jdbc.core.RowMapper;
import org.springframework.jdbc.core.namedparam.MapSqlParameterSource;
import org.springframework.jdbc.core.simple.SimpleJdbcCall;
import org.springframework.stereotype.Repository;

import java.sql.ResultSet;
import java.sql.SQLException;
import java.util.ArrayList;
import java.util.List;
import java.util.Map;

@Repository
public class CustomMovieRepository {
    private final JdbcTemplate jdbcTemplate;

    public CustomMovieRepository(JdbcTemplate jdbcTemplate) {
        this.jdbcTemplate = jdbcTemplate;
    }

    public MovieActorDTO getMovieWithActors(short movieId) {
        SimpleJdbcCall jdbcCall = new SimpleJdbcCall(jdbcTemplate)
                .withProcedureName("get_movie_with_actors")
                .returningResultSet("p_cursor", new RowMapper<MovieActorDTO>() {
                    final MovieActorDTO dto = new MovieActorDTO();
                    @Override
                    public MovieActorDTO mapRow(ResultSet rs, int rowNum) throws SQLException {
                        dto.setMovieId(rs.getLong("movie_id"));
                        dto.setMovieTitle(rs.getString("movie_title"));
                        dto.setMovieDescription(rs.getString("movie_description"));
                        List<ActorDTO> actors = dto.getActors();
                        if (actors == null) {
                            actors = new ArrayList<>();
                        }

                        ActorDTO actorDTO = new ActorDTO();
                        actorDTO.setActorId(rs.getShort("actor_id"));
                        actorDTO.setFirstName(rs.getString("actor_first_name"));
                        actorDTO.setLastName(rs.getString("actor_last_name"));
                        actors.add(actorDTO);

                        dto.setActors(actors);
                        return dto;
                    }
                });

        MapSqlParameterSource in = new MapSqlParameterSource()
                .addValue("p_movie_id", movieId);

        Map<String, Object> result = jdbcCall.execute(in);
        List<MovieActorDTO> list = (List<MovieActorDTO>) result.get("p_cursor");
        return list.get(0);
    }
}
`;

export const FakeJSON = `{
  key: "Deploy",
  input: {
    buildArchive: {
      location: "string",
      description: "The location of the build archive",
    },
    cloudProvider: {
      type: "string",
      description: "The target cloud provider for deployment",
      enum: ["AWS", "Azure", "GCP"],
    },
  },
  output: {
    terraformScript: {
      path: "string",
      description: "The generated Terraform script for deployment",
    },
  },
  steps: [
    {
      name: "Generate Terraform Script",
      type: "script",
      script: "generate_terraform_script.py",
      input: {
        buildArchive: "{{buildArchive}}",
        cloudProvider: "{{cloudProvider}}",
      },
      output: {
        terraformScript: "{{generatedScript}}",
      },
    },
    {
      name: "Execute Terraform Script",
      type: "command",
      command: "terraform apply",
      workingDirectory: "./deployment",
      environment: {
        TF_VAR_build_archive: "{{buildArchive}}",
        TF_VAR_cloud_provider: "{{cloudProvider}}",
      },
    },
  ],
}`;
